"use client";

import { useState } from "react";
import type { SessionCountdown } from "@/hooks/useSessionCountdown";
import type { McCopy } from "@/hooks/useFunnel";
import { useScrolledPast } from "@/hooks/useScrolledPast";
import { Countdown, LivePulse } from "./Countdown";
import { CalendarSlot } from "./CalendarSlot";
import { UseCases } from "./UseCases";
import { Icon } from "./Icon";
import {
  AUDIENCE,
  BONUS_TEASER,
  CLOSING,
  CTA_HINT,
  OBJECTIONS,
  PROGRAM,
  PROGRAM_TITLE,
  REFERENCES,
  TERMS_PROMISE,
  TERMS_TITLE,
} from "@/lib/masterclass";
import { capFirst, cn } from "@/lib/utils";

// ============================================================================
// Landing masterclass (/mc) — mobile first.
//   Lecture : imminence → promesse → créneau → CTA · avant/après · TERMS ·
//   bonus teasés · programme · pour qui + objections · clôture.
//   L'inscription reste accessible en permanence : bouton dans la barre du
//   haut, pastille flottante en bas à droite (desktop), barre pleine largeur
//   en bas (mobile).
//   Présentationnel : copie depuis @/lib/masterclass, variantes A/B et tracking
//   depuis useFunnel.
// ============================================================================

export function MasterclassLanding({
  countdown,
  mc,
  onRegister,
}: {
  countdown: SessionCountdown;
  mc: McCopy;
  onRegister: (location: string) => void;
}) {
  const scrolled = useScrolledPast(560);
  const when = countdown.live ? "maintenant" : countdown.proximity;

  return (
    <main className="w-full pb-28 sm:pb-0">
      {/* ---- Barre d'imminence + inscription -------------------------------- */}
      <div className="sticky top-0 z-40 w-full border-b border-ink/10 bg-cream/95 backdrop-blur">
        <div className="mx-auto flex max-w-2xl items-center justify-between gap-4 px-4 py-2.5">
          <p className="flex min-w-0 items-center gap-2 text-[11px] font-semibold leading-tight text-ink sm:text-xs">
            <LivePulse />
            <span className="truncate">
              {capFirst(when)} 12h30
            </span>
            {countdown.ready && !countdown.live && (
              <span className="hidden text-ink/45 sm:inline">·</span>
            )}
            <Countdown c={countdown} size="sm" className="hidden sm:inline" />
          </p>
          <button
            type="button"
            onClick={() => onRegister("header")}
            className="shrink-0 cursor-pointer rounded-lg bg-gold px-3.5 py-1.5 text-xs font-semibold text-ink transition hover:bg-gold-dark"
          >
            Réserver
          </button>
        </div>
      </div>

      {/* ---- Hero ----------------------------------------------------------- */}
      <section className="mx-auto w-full max-w-2xl px-5 pt-7 pb-10 text-center sm:pt-12">
        {/* Zone variante-dépendante : masquée tant que les flags A/B ne sont pas
            tranchés, sinon on affiche le control puis la bonne copie. */}
        <div
          className={cn(
            "transition-opacity duration-150",
            mc.ready ? "opacity-100" : "opacity-0"
          )}
        >
          <h1 className="text-balance text-[30px] font-extrabold leading-[1.1] tracking-tight sm:text-[46px]">
            {mc.headline}
          </h1>
          <p className="mx-auto mt-4 max-w-xl text-pretty text-[15px] leading-relaxed text-ink/70 sm:text-lg">
            {mc.tagline}
          </p>
          {mc.proof && (
            <p className="mx-auto mt-4 max-w-md text-pretty text-[13px] font-medium leading-snug text-ink/80 sm:text-sm">
              <Icon name="rise" className="mr-1.5 inline-block h-4 w-4 align-[-3px] text-brand" />
              {mc.proof}
            </p>
          )}
        </div>

        <CalendarSlot c={countdown} className="mt-7" />

        {/* Compteur et bouton sur une même ligne, franchement espacés. */}
        <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
          <Countdown c={countdown} />
          <Cta
            onClick={() => onRegister("hero")}
            className={cn(!mc.ready && "opacity-0")}
          >
            {mc.cta}
          </Cta>
        </div>
        <p className="mx-auto mt-5 max-w-[19rem] text-xs text-ink/50 sm:max-w-none">
          {CTA_HINT}
        </p>
      </section>

      {/* ---- Avant / après --------------------------------------------------- */}
      <Section tint>
        <UseCases />
      </Section>

      {/* ---- La promesse, levier par levier (TERMS) --------------------------- */}
      <Section>
        <H2>{TERMS_TITLE}</H2>
        {/* Cartes sur 2 colonnes à partir de sm. Sur mobile on reste en une
            colonne : à 375 px, deux cartes côte à côte rendraient ces phrases
            illisibles. Statut, cinquième et donc orphelin, occupe la largeur. */}
        <dl className="mt-7 grid gap-3.5 sm:grid-cols-2">
          {TERMS_PROMISE.map((t, i) => (
            <div
              key={t.levier}
              className={cn(
                "rounded-2xl border border-ink/10 bg-white p-5",
                i === TERMS_PROMISE.length - 1 && "sm:col-span-2"
              )}
            >
              <dt className="flex items-center gap-2.5">
                <span className="text-brand">
                  <Icon name={t.icon} className="h-6 w-6" />
                </span>
                <span className="text-xs font-bold uppercase tracking-wide text-brand">
                  {t.levier}
                </span>
              </dt>
              <dd className="mt-2.5 text-[15px] leading-relaxed text-ink/80">
                {t.line}
              </dd>
            </div>
          ))}
        </dl>
      </Section>

      {/* ---- Les bonus, annoncés sans être dévoilés --------------------------- */}
      <Section tint>
        <Eyebrow>{BONUS_TEASER.eyebrow}</Eyebrow>
        <H2>{BONUS_TEASER.title}</H2>
        <p className="mt-4 text-[15px] leading-relaxed text-ink/70">
          {BONUS_TEASER.body}
        </p>
      </Section>

      {/* ---- Le programme, en timeline ---------------------------------------- */}
      <Section>
        <H2>{PROGRAM_TITLE}</H2>
        <ol className="mt-7">
          {PROGRAM.map((p, i) => (
            <li key={p.title} className="relative flex gap-4 pb-7 last:pb-0">
              {/* Le trait qui relie les étapes, sauf après la dernière */}
              {i < PROGRAM.length - 1 && (
                <span
                  aria-hidden
                  className="absolute left-[19px] top-10 bottom-1 w-px bg-brand/25"
                />
              )}
              <span className="relative z-10 grid h-10 w-10 shrink-0 place-items-center rounded-full border border-brand/25 bg-brand/10 text-brand">
                <Icon name={p.icon} className="h-5 w-5" />
              </span>
              <div className="min-w-0 flex-1 pt-1">
                <p className="text-[15px] font-semibold text-ink">{p.title}</p>
                <p className="mt-1 text-sm leading-relaxed text-ink/65">
                  {p.detail}
                </p>
              </div>
            </li>
          ))}
        </ol>
      </Section>

      {/* ---- Pour qui + les objections ----------------------------------------- */}
      <Section tint>
        <H2>{AUDIENCE.title}</H2>
        <p className="mt-4 text-[15px] leading-relaxed text-ink/70">
          {AUDIENCE.intro}
        </p>
        <div className="mt-6 overflow-hidden rounded-2xl border border-ink/10 bg-white">
          {OBJECTIONS.map((o, i) => (
            <Accordion key={o.q} q={o.q} a={o.a} first={i === 0} />
          ))}
        </div>
        {REFERENCES.length > 0 && (
          <p className="mt-6 text-sm text-ink/55">
            Nous sommes intervenus auprès de {REFERENCES.join(", ")}.
          </p>
        )}
      </Section>

      {/* ---- Clôture ------------------------------------------------------------ */}
      <section className="mx-auto w-full max-w-2xl px-5 pt-14 pb-12 text-center sm:pt-16">
        <h2 className="text-balance text-[26px] font-extrabold leading-tight sm:text-3xl">
          {CLOSING.title}
        </h2>
        <p className="mx-auto mt-4 max-w-lg text-pretty text-[15px] leading-relaxed text-ink/70">
          {CLOSING.body}
        </p>

        <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
          <Countdown c={countdown} />
          <Cta onClick={() => onRegister("closing")}>{mc.cta}</Cta>
        </div>
        <p className="mx-auto mt-5 max-w-[19rem] text-xs text-ink/50 sm:max-w-none">
          {CTA_HINT}
        </p>
      </section>

      {/* ---- Pastille flottante (desktop) : inscription toujours à portée ------ */}
      <button
        type="button"
        onClick={() => onRegister("floating")}
        className={cn(
          "fixed bottom-6 right-6 z-40 hidden cursor-pointer items-center gap-2.5 rounded-full bg-gold px-6 py-3.5 text-sm font-semibold text-ink shadow-xl shadow-ink/20 transition hover:bg-gold-dark hover:scale-[1.03] sm:flex",
          scrolled ? "translate-y-0 opacity-100" : "pointer-events-none translate-y-3 opacity-0"
        )}
      >
        <LivePulse />
        {mc.cta}
      </button>

      {/* ---- Barre d'inscription (mobile) -------------------------------------- */}
      <div
        className={cn(
          "fixed inset-x-0 bottom-0 z-40 border-t border-ink/10 bg-cream/95 px-4 pt-3.5 pb-4 backdrop-blur transition-transform duration-200 sm:hidden",
          scrolled ? "translate-y-0" : "translate-y-full"
        )}
      >
        <button
          type="button"
          onClick={() => onRegister("sticky_mobile")}
          className="flex w-full cursor-pointer items-center justify-center gap-2 rounded-xl bg-gold px-6 py-3.5 text-base font-semibold text-ink shadow-lg shadow-gold/25 transition active:scale-[0.99]"
        >
          <LivePulse />
          {mc.cta}
        </button>
        <p className="mt-2.5 flex items-center justify-center gap-1.5 text-[11px] text-ink/50">
          {capFirst(when)} 12h30 · sans replay
          {countdown.ready && !countdown.live && (
            <>
              · <Countdown c={countdown} size="sm" />
            </>
          )}
        </p>
      </div>
    </main>
  );
}

// ===== petits composants ====================================================

function Accordion({ q, a, first }: { q: string; a: string; first: boolean }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={cn("border-ink/10", !first && "border-t")}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full cursor-pointer items-center justify-between gap-4 px-5 py-4 text-left"
      >
        <span className="text-[15px] font-semibold text-ink">{q}</span>
        <span
          aria-hidden
          className={cn(
            "shrink-0 text-lg leading-none text-brand transition-transform duration-200",
            open && "rotate-45"
          )}
        >
          +
        </span>
      </button>
      {open && (
        <p className="px-5 pb-5 text-sm leading-relaxed text-ink/65">{a}</p>
      )}
    </div>
  );
}

function Cta({
  children,
  onClick,
  className,
}: {
  children: React.ReactNode;
  onClick: () => void;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "inline-flex w-full cursor-pointer items-center justify-center rounded-xl bg-gold px-7 py-4 text-base font-semibold text-ink shadow-lg shadow-gold/25 transition hover:bg-gold-dark active:scale-[0.99] sm:w-auto sm:hover:scale-[1.02]",
        className
      )}
    >
      {children}
    </button>
  );
}

function Section({ children, tint = false }: { children: React.ReactNode; tint?: boolean }) {
  return (
    <section className={cn("w-full", tint && "bg-cream-dark/40")}>
      <div className="mx-auto w-full max-w-2xl px-5 py-12 sm:py-14">{children}</div>
    </section>
  );
}

const Eyebrow = ({ children }: { children: React.ReactNode }) => (
  <p className="text-xs font-semibold uppercase tracking-wide text-brand">{children}</p>
);

const H2 = ({ children }: { children: React.ReactNode }) => (
  <h2 className="mt-2 text-balance text-[26px] font-extrabold leading-tight tracking-tight sm:text-[32px]">
    {children}
  </h2>
);
